import axios from "axios";
import { SUBDOMAIN_NAME_STORAGE_KEY } from "../constants/subdomainName";

const TOKEN_KEY = "api_access_token";
const REFRESH_TOKEN_KEY = "api_refresh_token";
const REFRESH_URL = process.env.REACT_APP_API_BASE_URL + "/v1/token/refresh";
const getAuth0TokenExchangeUrl = () => {
  const custom = process.env.REACT_APP_AUTH0_TOKEN_EXCHANGE_URL;
  if (custom && custom.startsWith("http")) return custom;
  const base = process.env.REACT_APP_API_BASE_URL || "";
  return base + (custom || "/v1/auth0/token");
};

/** Deep clone API JSON and mask JWT strings for safe console debugging */
const maskTokenInPayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload;
  try {
    const out = JSON.parse(JSON.stringify(payload));
    if (out.body && typeof out.body.token === "string") {
      out.body.token = `[REDACTED len=${out.body.token.length}]`;
    }
    if (typeof out.token === "string") {
      out.token = `[REDACTED len=${out.token.length}]`;
    }
    return out;
  } catch {
    return { _note: "could not serialize response" };
  }
};

let isRefreshing = false;
let requestQueue = [];

const processQueue = (error, token = null) => {
  requestQueue.forEach(p => {
    if (error) p.reject(error);
    else p.resolve(token);
  });
  requestQueue = [];
};

let refreshWithAuth0Callback = null;
let tokenChangeListeners = [];

/**
 * Extracts the token and refresh_token from the API Gateway wrapped response.
 * The backend wraps responses as { statusCode, body, msgCode, ... }.
 * Throws with a descriptive error when the backend reports a failure.
 */
const extractTokensFromResponse = (data) => {
  const statusCode = data?.statusCode;
  if (statusCode && statusCode >= 400) {
    const description =
      data?.msgCode?.[0]?.description ||
      data?.body?.msgCode?.[0]?.description ||
      `Backend returned status ${statusCode}`;
    throw new Error(description);
  }

  const token = data?.body?.token || data?.token;
  const refreshToken = data?.body?.refresh_token || data?.refresh_token;

  if (!token) {
    throw new Error("No token in backend response");
  }

  return { token, refreshToken };
};

export const tokenService = {
  getToken: () => localStorage.getItem(TOKEN_KEY),

  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),

  clearToken: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    tokenChangeListeners.forEach((cb) => {
      try { cb(null); } catch (e) {
        console.error("[TokenService] Error in token change listener:", e);
      }
    });
  },

  setToken: (token) => {
    localStorage.setItem(TOKEN_KEY, token);
    tokenChangeListeners.forEach((cb) => {
      try { cb(token); } catch (e) {
        console.error("[TokenService] Error in token change listener:", e);
      }
    });
  },

  setRefreshToken: (refreshToken) => {
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  },

  onTokenChange: (callback) => {
    tokenChangeListeners.push(callback);
    return () => {
      tokenChangeListeners = tokenChangeListeners.filter((cb) => cb !== callback);
    };
  },

  getTokenExpiresInMs: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (!payload.exp) return null;
      return payload.exp * 1000 - Date.now();
    } catch {
      return null;
    }
  },

  setRefreshWithAuth0: (callback) => {
    refreshWithAuth0Callback = callback;
  },

  /**
   * Exchange Auth0 access token for internal backend JWT.
   * Stores both the access token and refresh_token from the backend.
   */
  fetchTokenFromAuth0: async (auth0AccessToken, tenantUuid) => {
    const url = getAuth0TokenExchangeUrl();
    const body = {};
    const subdomainName = localStorage.getItem(SUBDOMAIN_NAME_STORAGE_KEY);
    if (subdomainName) {
      body.subdomain_name = subdomainName;
    }
    if (tenantUuid) {
      body.tenant_uuid = tenantUuid;
    }

    if (process.env.NODE_ENV === "development") {
      console.info("[TokenService] Auth0 → API token exchange request", {
        url,
        body,
        tenantUuidArg: tenantUuid || null,
        workspaceUuidInLocalStorage: localStorage.getItem("workspace_uuid"),
        env_REACT_APP_TENANT_UUID: process.env.REACT_APP_TENANT_UUID || null,
      });
    }

    const { data } = await axios.post(url, body, {
      headers: { Authorization: `Bearer ${auth0AccessToken}` },
    });

    if (process.env.NODE_ENV === "development") {
      const masked = maskTokenInPayload(data);
      console.info("[TokenService] Auth0 → API token exchange response (raw shape)", masked);
    }

    const { token, refreshToken } = extractTokensFromResponse(data);
    tokenService.setToken(token);
    tokenService.setRefreshToken(refreshToken);
    return token;
  },

  /**
   * Refresh using the backend's refresh_token (POST /v1/token/refresh).
   * This is the primary refresh mechanism — lightweight, no Auth0 dependency.
   * Returns the new access token.
   */
  refreshWithBackendToken: async () => {
    const refreshToken = tokenService.getRefreshToken();
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const { data } = await axios.post(REFRESH_URL, {
      refresh_token: refreshToken,
    });

    const { token, refreshToken: newRefreshToken } = extractTokensFromResponse(data);
    tokenService.setToken(token);
    tokenService.setRefreshToken(newRefreshToken);
    return token;
  },

  /**
   * Primary refresh: try backend refresh_token first, fall back to Auth0 re-exchange.
   * Single-flight queued to avoid duplicate parallel refreshes.
   */
  refreshTokenAuth0: async (tenantUuid) => {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        requestQueue.push({ resolve, reject });
      });
    }

    isRefreshing = true;
    try {
      const token = await tokenService.refreshWithBackendToken();
      processQueue(null, token);
      return token;
    } catch (backendErr) {
      console.warn("[TokenService] Backend refresh failed, trying Auth0 fallback:", backendErr.message);

      if (!refreshWithAuth0Callback) {
        const err = new Error("Both backend refresh and Auth0 fallback unavailable");
        processQueue(err, null);
        throw err;
      }

      try {
        const token = await refreshWithAuth0Callback(tenantUuid);
        processQueue(null, token);
        return token;
      } catch (auth0Err) {
        processQueue(auth0Err, null);
        throw auth0Err;
      }
    } finally {
      isRefreshing = false;
    }
  },
};
